import { Injectable } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';
import { Banner, BannerName } from '../models/banner.model';
import { ConfigService } from './config.service';
import { HttpClient } from '@angular/common/http';

type BannerData<Name extends string> = {
  [K in `${Name}-img` | `${Name}-url`]: string;
};

type BannerResponse = {
  applicationTitle: string;
} & {
  [K in BannerName]: BannerData<K>;
};

@Injectable({
  providedIn: 'root',
})
export class BannerService {
  public readonly bannerData$: Observable<Record<BannerName, Banner>>;

  public constructor(
    private readonly http: HttpClient,
    configService: ConfigService,
  ) {
    const bannerUrl = configService.config.advertisingBannersUrl;
    this.bannerData$ = this.http.get<BannerResponse>(bannerUrl).pipe(
      map(this.parseBannerData.bind(this)),
      shareReplay(1),
    );
  }

  public getBannerByName(bannerName: BannerName): Observable<Banner | undefined> {
    return this.bannerData$.pipe(map(bannerData => bannerData[bannerName]));
  }

  private parseBannerData(raw: BannerResponse): Record<BannerName, Banner> {
    return Object.fromEntries(
      Object.entries(raw)
        .filter(([k]) => k !== 'applicationTitle')
        .map(([k, v]: [BannerName, BannerData<any>]) => [k, this.toBanner(k, v)]),
    ) as Record<BannerName, Banner>;
  }

  private toBanner<Name extends BannerName>(name: Name, bannerData: BannerData<Name>): Banner {
    return {
      name,
      imageUrl: bannerData[`${name}-img`],
      link: bannerData[`${name}-url`],
    };
  }
}
